import { Select } from "@package/subframe/components/Select";
import { useState } from "react";
import { toast } from "sonner";

import { ProfileSectionDialog } from "@/components/people/dialogs/ProfileSectionDialog";
import { useUpdateMemberRoleCommandControllerUpdateMemberRole } from "@/generated/members/members";
import type { NurseryRole } from "@/generated/model/nurseryRole";
import type { UpdateMemberRoleDtoRole } from "@/generated/model/updateMemberRoleDtoRole";
import { NURSERY_ROLE_DESCRIPTIONS, nurseryRoleLabel } from "@/lib/roles";

import { accountActionErrorMessage } from "./account-action-error";
import { useInvalidateAccount } from "./use-invalidate-account";

interface ChangeAccessRoleDialogProps {
  nurseryId: string;
  userId: string;
  displayName: string;
  currentRole: NurseryRole;
  /** From assignableRoles(): a nursery manager is never offered Manager. */
  roles: UpdateMemberRoleDtoRole[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Change someone's role at this nursery, which sets what they can access. */
export function ChangeAccessRoleDialog({
  nurseryId,
  userId,
  displayName,
  currentRole,
  roles,
  open,
  onOpenChange,
}: ChangeAccessRoleDialogProps) {
  const [role, setRole] = useState<string>(currentRole);
  const updateRole = useUpdateMemberRoleCommandControllerUpdateMemberRole();
  const invalidateAccount = useInvalidateAccount();

  const handleOpenChange = (next: boolean) => {
    if (!next) setRole(currentRole);
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (role === currentRole) {
      onOpenChange(false);
      return;
    }
    try {
      await updateRole.mutateAsync({
        nurseryId,
        userId,
        data: { role: role as UpdateMemberRoleDtoRole },
      });
      toast.success(
        `${displayName} is now a ${nurseryRoleLabel(role as NurseryRole)}`,
      );
      onOpenChange(false);
      await invalidateAccount(userId);
    } catch (error) {
      toast.error(
        accountActionErrorMessage(
          error,
          `Could not change ${displayName}'s role`,
        ),
      );
    }
  };

  return (
    <ProfileSectionDialog
      title="Change access role"
      testId="change-access-role-dialog"
      open={open}
      onOpenChange={handleOpenChange}
      isPending={updateRole.isPending}
      onSubmit={() => void handleSubmit()}
    >
      <Select
        label="Access role"
        value={role}
        onValueChange={(value) => value && setRole(value)}
        disabled={updateRole.isPending}
        helpText={NURSERY_ROLE_DESCRIPTIONS[role]}
      >
        {roles.map((option) => (
          <Select.Item key={option} value={option}>
            {nurseryRoleLabel(option)}
          </Select.Item>
        ))}
      </Select>
    </ProfileSectionDialog>
  );
}
